import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";

// Authority grants: who may administer what, and how far that reaches.
// Nothing in the agent app reads these yet — this is the curation surface, so
// the data can be made right before any permission check moves onto it.

export const useGetRoleGrantsQuery = (filters = {}) => {
  const params = new URLSearchParams(
    Object.entries(filters).filter(([, value]) => value),
  ).toString();

  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_ROLE_GRANTS, params],
    queryFn: () =>
      api.get(
        params
          ? `${API_ROUTES.GET_ROLE_GRANTS}?${params}`
          : API_ROUTES.GET_ROLE_GRANTS,
      ),
  });

  return { data: data?.data?.data ?? [], isPending, isError, error };
};

// Every dropdown the grant form needs, in one call. The scope lists change
// rarely, so they hold for the session rather than refetching per modal open.
export const useGetRoleGrantOptionsQuery = () => {
  const { data, isPending } = useQuery({
    queryKey: [API_ROUTES.GET_ROLE_GRANT_OPTIONS],
    queryFn: () => api.get(API_ROUTES.GET_ROLE_GRANT_OPTIONS),
    staleTime: Infinity,
  });

  return { data: data?.data?.data ?? null, isPending };
};

// What a person can actually reach once inheritance is applied — a group grant
// covers companies that have no row of their own, so the rows alone don't say.
export const useGetUserAuthorityQuery = (userId) => {
  const { data, isPending } = useQuery({
    queryKey: [API_ROUTES.GET_ROLE_GRANTS, "authority", userId],
    queryFn: () => api.get(`${API_ROUTES.GET_ROLE_GRANTS}/user/${userId}`),
    enabled: !!userId,
  });

  return { data: data?.data?.data ?? null, isPending };
};

const invalidate = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: [API_ROUTES.GET_ROLE_GRANTS] });
};

export const useCreateRoleGrantMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (data) => api.post(API_ROUTES.GET_ROLE_GRANTS, data),
    onSuccess: () => showToast("success", "Grant created"),
    // The two unique indexes refuse a duplicate grant and a second primary
    // owner. Those messages name the rule that was broken — keep them.
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to create grant",
      ),
    onSettled: () => invalidate(queryClient),
  });

  return { mutate, isPending };
};

// By email rather than an existing person — creates the User first if this is
// their first grant, and emails them a web login link. The new User this can
// create is why it invalidates the options list too: the next invite's
// "Person" picker (RoleGrantEdit) should be able to find them.
export const useInviteRoleGrantMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (data) => api.post(`${API_ROUTES.GET_ROLE_GRANTS}/invite`, data),
    onSuccess: (res) => {
      const { isNewUser, emailSent } = res?.data?.data || {};
      if (!emailSent) {
        showToast("error", "Grant created, but the invite email failed to send.");
      } else {
        showToast("success", isNewUser ? "Invite sent" : "Invite sent — they already had an account");
      }
    },
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to send the invite",
      ),
    onSettled: () => {
      invalidate(queryClient);
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.GET_ROLE_GRANT_OPTIONS] });
    },
  });

  return { mutate, isPending };
};

export const useUpdateRoleGrantMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, ...data }) =>
      api.put(`${API_ROUTES.GET_ROLE_GRANTS}/${id}`, data),
    onSuccess: () => showToast("success", "Grant updated"),
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to update grant",
      ),
    onSettled: () => invalidate(queryClient),
  });

  return { mutate, isPending };
};

export const useDeleteRoleGrantMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (id) => api.delete(`${API_ROUTES.GET_ROLE_GRANTS}/${id}`),
    onSuccess: () => showToast("success", "Grant deleted"),
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to delete grant",
      ),
    onSettled: () => invalidate(queryClient),
  });

  return { mutate, isPending };
};
